import React, { Suspense, useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Divider,
  Typography,
  ListItemIcon,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Assignment as AssignmentIcon,
  RequestQuote as RequestQuoteIcon,
  Warehouse as WarehouseIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@contexts/AuthContext";
import { useEmpresa } from "@contexts/EmpresaContext";
import { resolveAvatarUrl } from "@utils/avatar";
import AppShell from "@components/layout/AppShell";
import ErroDeCarregamento from "@components/common/ErroDeCarregamento";
import LoadingScreen from "@components/common/LoadingScreen";
import TopBar from "@components/layout/TopBar";
import SideNav from "@components/layout/SideNav";
import { menuVisivel } from "@components/layout/menuAcesso";
import {
  MENU_OCULTO,
  MENU_RAIL,
  estadoEfetivoDoMenu,
  gravarEstadoMenu,
  larguraDoMenu,
  lerEstadoMenu,
  proximoEstadoMenu,
} from "@components/layout/menuLateral";

const drawerWidth = 260;
const drawerWidthMobile = 220;
const drawerWidthCollapsed = 72;

// Itens de menu. O campo `papeis`, quando presente, restringe o item aos papéis
// listados dentro da empresa ativa (matriz de permissões da issue #45). Itens
// sem `papeis` permanecem visíveis para qualquer usuário logado.
const menuItems = [
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
    role: "all",
    // GET /relatorios/dashboard exige administrador; para os operadores a
    // tela inicial é a da sua área (rotaInicialPorPapel).
    papeis: ["administrador"],
  },
  {
    text: "Clientes",
    icon: <PeopleIcon />,
    path: "/clientes",
    role: "all",
    papeis: ["administrador"],
  },
  {
    text: "Produtos",
    icon: <InventoryIcon />,
    path: "/produtos",
    role: "all",
    papeis: ["administrador", "operador_estoque"],
  },
  {
    text: "Grupos de Produtos",
    icon: <CategoryIcon />,
    path: "/grupos-produto",
    role: "all",
    papeis: ["administrador", "operador_estoque"],
  },
  {
    text: "Pedidos",
    icon: <AssignmentIcon />,
    path: "/pedidos",
    role: "all",
    papeis: ["administrador", "operador_entrega"],
  },
  {
    text: "Orçamentos",
    icon: <RequestQuoteIcon />,
    path: "/orcamentos",
    role: "all",
    papeis: ["administrador"],
  },
  {
    text: "Estoque",
    icon: <WarehouseIcon />,
    path: "/estoque",
    role: "all",
    papeis: ["administrador", "operador_estoque"],
  },
  {
    text: "Relatórios",
    icon: <AssessmentIcon />,
    path: "/relatorios",
    role: "all",
    papeis: ["administrador"],
  },
  {
    text: "Empresas",
    icon: <BusinessIcon />,
    path: "/empresa",
    role: "all",
  },
  {
    text: "Configurações",
    icon: <SettingsIcon />,
    path: "/configuracoes",
    role: "admin",
  },
];

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { papelNaEmpresaAtiva, loading: empresaLoading } = useEmpresa();
  const appTitle =
    import.meta.env.VITE_APP_SYS_TITLE || import.meta.env.VITE_APP_ENTER_TITLE;
  const resolvedAvatarUrl = resolveAvatarUrl(user?.avatarUrl);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [estadoMenu, setEstadoMenu] = useState(() => lerEstadoMenu());
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    if (isMobile) {
      return;
    }
    const proximo = proximoEstadoMenu(estadoMenu);
    setEstadoMenu(proximo);
    gravarEstadoMenu(proximo);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleProfile = () => {
    navigate("/perfil");
    handleProfileMenuClose();
  };

  const filteredMenuItems = menuItems.filter((item) =>
    menuVisivel(item, {
      isAdmin: isAdmin(),
      papel: papelNaEmpresaAtiva,
      empresaLoading,
    }),
  );

  const estadoMenuEfetivo = estadoEfetivoDoMenu(estadoMenu, isMobile);
  const effectiveNavCollapsed = estadoMenuEfetivo === MENU_RAIL;
  const effectiveNavHidden = estadoMenuEfetivo === MENU_OCULTO;

  const effectiveDrawerWidth = larguraDoMenu(estadoMenuEfetivo, {
    expandida: drawerWidth,
    rail: drawerWidthCollapsed,
  });

  const topBar = (
    <TopBar
      onOpenDrawer={handleDrawerToggle}
      onOpenNotifications={handleNotificationsOpen}
      onOpenProfile={handleProfileMenuOpen}
      onToggleCollapse={handleCollapseToggle}
      estadoMenu={estadoMenuEfetivo}
      mobileOpen={mobileOpen}
      drawerWidth={effectiveDrawerWidth}
      avatarUrl={resolvedAvatarUrl}
      avatarFallback={user?.name?.charAt(0)?.toUpperCase() || "U"}
    />
  );

  const sideNav = (
    <SideNav
      menuItems={filteredMenuItems}
      isMobile={isMobile}
      mobileOpen={mobileOpen}
      onToggle={handleDrawerToggle}
      onNavigate={handleMenuClick}
      activePath={location.pathname}
      appTitle={appTitle}
      drawerWidth={drawerWidth}
      drawerWidthMobile={drawerWidthMobile}
      collapsedWidth={drawerWidthCollapsed}
      isCollapsed={effectiveNavCollapsed}
      isHidden={effectiveNavHidden}
    />
  );

  return (
    <Box>
      <AppShell
        topBar={topBar}
        sideNav={sideNav}
        navWidth={effectiveDrawerWidth}
      >
        {/* As páginas são baixadas sob demanda: o menu e a barra ficam na tela
            enquanto o código da rota chega (ou avisam, se ele não chegar). */}
        <ErroDeCarregamento resetKey={location.pathname}>
          <Suspense fallback={<LoadingScreen minHeight="50vh" />}>
            <Outlet />
          </Suspense>
        </ErroDeCarregamento>
      </AppShell>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: { mt: 1, minWidth: 200 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {user?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />

        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          Meu Perfil
        </MenuItem>

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sair
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: { mt: 1, minWidth: 300, maxWidth: 400 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="h6">Notificações</Typography>
        </Box>
        <Divider />

        <MenuItem>
          <Typography variant="body2" color="text.secondary">
            Nenhuma notificação
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
